import { IconFileAlt, IconInfoCircle, IconTimes, mapper } from '@maia/icons';
import parse from 'html-react-parser';
import { formatCurrency } from '@maia/utils';
import { useId } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  BigPricingPill,
  Button,
  cardStyles,
  Divider,
  FeaturedIcon,
  TextButton,
  Typography,
  AccordionGroup,
} from '../../ui';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
} from 'react-aria-components';
import { BillingUnitDto } from '@maia/shared';

type Item = {
  product: string;
  specs: Array<string>;
  icon: string;
  price: number;
  documents: Array<{
    type: string;
    filename: string;
    mimetype: string;
    path: string;
  }>;
  clauses: Array<{
    name: string;
    content: string;
    condition: string | null;
  }>;
};

type Props = {
  items: Array<Item>;
  totalPrice: number;
  tax: number;
  billingUnit: BillingUnitDto;
};

export function Receipt(props: Props) {
  const id = useId();
  const { items, tax, totalPrice, billingUnit } = props;

  return (
    <div>
      <div
        className={twMerge(
          cardStyles({ variant: 'outlined' }),
          'p-24 pb-48 rounded-b-none border-b-0',
        )}
      >
        <ul className="space-y-12">
          {items?.map((item, key, all) => {
            const hasDocumentsOrClauses =
              item.clauses.length > 0 || item.documents.length > 0;

            return (
              <li key={`item-${id}-${key}`} className="flex flex-col gap-y-24">
                <div className="flex flex-row">
                  <FeaturedIcon
                    icon={mapper(item.icon)}
                    variant="light-square"
                    size="xs"
                  />

                  <div
                    className={twMerge(
                      'ml-12 flex-1',
                      !hasDocumentsOrClauses && 'flex items-center',
                    )}
                  >
                    <Typography as="p" variant="body-xs" weight="medium">
                      {item.product}
                    </Typography>
                    {item.specs.length > 0 && (
                      <ul>
                        {item.specs.map((spec, key2) => (
                          <Typography
                            as="li"
                            variant="body-xs"
                            weight="regular"
                            className="text-grayscale400"
                            key={`item-${id}-${key}-spec-${key2}`}
                          >
                            {spec}
                          </Typography>
                        ))}
                      </ul>
                    )}
                  </div>

                  <Typography variant="body-xs" weight="semibold">
                    {formatCurrency(item.price)}{' '}
                    <Typography
                      as="span"
                      variant="body-xs"
                      weight="regular"
                      className="text-grayscale400"
                    >
                      {billingUnit.label.toLowerCase()}
                    </Typography>
                  </Typography>
                </div>

                {hasDocumentsOrClauses && (
                  <div className="flex flex-row gap-24">
                    <div className="flex flex-1 gap-8 flex-wrap">
                      {item.documents.map((document, key2) => (
                        <a
                          target="_blank"
                          className="pointer-events-auto max-sm:col-span-2 sm:w-fit"
                          title={document.filename}
                          href={document.path}
                          key={`item-${id}-${key}-document-${key2}`}
                          rel="noreferrer"
                        >
                          <Button
                            intent="secondary"
                            size="small"
                            iconLeft={IconFileAlt}
                          >
                            {document.filename}
                          </Button>
                        </a>
                      ))}
                    </div>

                    {item.clauses.length > 0 && (
                      <AriaDialogTrigger>
                        <TextButton
                          intent="standard"
                          className="underline"
                          size="small"
                          iconRight={IconInfoCircle}
                        >
                          Clausules
                        </TextButton>
                        <AriaModal className="fixed inset-0 grid place-content-center size-full bg-grayscale0/50 backdrop-blur-sm">
                          <AriaDialog className="flex bg-grayscale0 size-fit p-32 shadow-1 max-w-[920px] max-h-[70vh] rounded-18">
                            <div className="space-y-24 overflow-y-scroll">
                              {item.clauses.map((clause, key2) => (
                                <AccordionGroup
                                  title={clause.name}
                                  content={
                                    <Typography
                                      as="div"
                                      variant="body-s"
                                      weight="regular"
                                      className="text-grayscale600 space-y-12 [&>ol]:ml-24 [&>ol]:list-outside [&>ol]:list-decimal [&>ol]:space-y-4 [&>ol]:py-4 [&>ul]:ml-24 [&>ul]:list-outside [&>ul]:list-disc [&>ul]:space-y-4 [&>ul]:py-4"
                                    >
                                      {parse(clause.content)}
                                    </Typography>
                                  }
                                  key={`item-${id}-${key}-clause-${key2}`}
                                />
                              ))}
                            </div>
                            <TextButton slot="close" className="ml-auto h-fit">
                              <IconTimes className="size-24" />
                            </TextButton>
                          </AriaDialog>
                        </AriaModal>
                      </AriaDialogTrigger>
                    )}
                  </div>
                )}

                {key < all.length - 1 && <Divider className="mb-24" />}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="flex items-center justify-between bg-primary50 p-20 pt-32 border border-t-0 border-grayscale100 rounded-b-12 zigzag -mt-12">
        <div className="flex flex-col gap-y-8">
          <Typography variant="body-m" weight="medium">
            Totaalpremie
          </Typography>
          {tax && (
            <Typography variant="body-xs" weight="regular">
              Incl. {formatCurrency(tax)} assurantiebelasting
            </Typography>
          )}
        </div>
        <BigPricingPill
          size="md"
          variant="ghost"
          price={totalPrice}
          billingUnit={billingUnit}
        />
      </div>
    </div>
  );
}
